import { Bar3, Pie1, Shield2 } from '@ucr/icons/main';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { getMedOrgName } from 'entities/MegOrgs';
import { usePermissions } from 'entities/Role/lib/hooks';
import { isRoleKey } from 'entities/Role/types';
import { TUser } from 'entities/User';

import { apiClient } from 'shared/api';

import { MenuItem } from './menu.types';

const MANAGE_PATH = '/manage';
const STATS_PATH = '/stats';

export const useMenu = (currentUser: TUser) => {
  const { pathname } = useLocation();

  const { getCurrentRolePermissions } = usePermissions(
    isRoleKey(currentUser.role.key) ? currentUser.role.key : undefined,
  );

  /** Проверка на возможность управления медорганизациями */
  const canManageMedicalOrgs = useMemo(
    () =>
      !!getCurrentRolePermissions()?.find((p) => p.key === 'medorgs.update'),
    [getCurrentRolePermissions],
  );

  /** Медорганизация текущего пользователя */
  const currentUserMedOrg = useMemo(() => {
    return currentUser.medorg;
  }, [currentUser]);

  const manageItems: MenuItem[] = useMemo(() => {
    return [
      canManageMedicalOrgs
        ? { title: 'Медорганизации', link: `${MANAGE_PATH}/medical_orgs` }
        : null,
      currentUserMedOrg
        ? {
            title: getMedOrgName(currentUserMedOrg) || 'Медорганизация',
            link: `${MANAGE_PATH}/medical_orgs/${currentUserMedOrg.id}`,
          }
        : null,
      {
        title: 'Типы медицинских изделий',
        link: `${MANAGE_PATH}/medical_devices_types`,
      },
      { title: 'Пользователи', link: `${MANAGE_PATH}/users` },
    ].filter((item): item is MenuItem => Boolean(item));
  }, [canManageMedicalOrgs, currentUserMedOrg]);

  const fetchStats = useCallback(async () => {
    const res = await apiClient.datalensControllerWebList();
    return res.reports;
  }, []);

  const reportItems = useMemo(async () => {
    try {
      const stats = await fetchStats();
      return stats.map((report) => ({
        title: report.title,
        link: `${STATS_PATH}/${report.sourceId}`,
      }));
    } catch (err) {
      console.error(err);
      return [];
    }
  }, [fetchStats]);

  const menu = useMemo(() => {
    return [
      {
        title: 'Управление',
        icon: Shield2,
        children: manageItems,
        disabled: false,
      },
      {
        title: 'Статистика',
        icon: Bar3,
        children: reportItems,
        disabled: false,
      },
      {
        title: 'Отчёты',
        icon: Pie1,
        children: [],
        disabled: true,
      },
    ];
  }, [manageItems, reportItems]);

  const [currentGroup, setCurrentGroup] = useState<string>(menu[0].title);

  const _setCurrentGroup = useCallback((group: string) => {
    setCurrentGroup(group);
  }, []);

  useEffect(() => {
    if (pathname.startsWith(MANAGE_PATH)) {
      _setCurrentGroup(menu[0].title);
    } else if (pathname.startsWith(STATS_PATH)) {
      _setCurrentGroup(menu[1].title);
    } else {
      // default
      _setCurrentGroup(menu[0].title);
    }
  }, [pathname, _setCurrentGroup]);

  return { menu, currentGroup, setCurrentGroup };
};
